import { useLayoutEffect, useRef, useState } from "react";
import { motion, useReducedMotion, type Variants } from "framer-motion";
import { gapContainer, gapItem, gapItemStatic, gapViewport } from "@/components/site/gap-motion";

import k04Src from "@/assets/Key_Image-04.png";
const k04 = { url: k04Src };
import k05Src from "@/assets/Key_Image-05.png";
const k05 = { url: k05Src };
import k06Src from "@/assets/Key_Image-06.png";
const k06 = { url: k06Src };
import k07Src from "@/assets/Key_Image-07.png";
const k07 = { url: k07Src };
import k08Src from "@/assets/Key_Image-08.png";
const k08 = { url: k08Src };
import k09Src from "@/assets/Key_Image-09.png";
const k09 = { url: k09Src };
import k10Src from "@/assets/Key_Image-10.png";
const k10 = { url: k10Src };
import k11Src from "@/assets/Key_Image-11.png";
const k11 = { url: k11Src };
import k12Src from "@/assets/Key_Image-12.png";
const k12 = { url: k12Src };
import k13Src from "@/assets/Key_Image-13.png";
const k13 = { url: k13Src };
import k14Src from "@/assets/Key_Image-14.png";
const k14 = { url: k14Src };

const items = [
  { src: k04.url, word: "Accountability" },
  { src: k05.url, word: "Consistency" },
  { src: k06.url, word: "Confidence" },
  { src: k07.url, word: "Communication" },
  { src: k08.url, word: "Resilience" },
  { src: k09.url, word: "Engagement" },
  { src: k10.url, word: "Growth" },
  { src: k11.url, word: "Mindset" },
  { src: k12.url, word: "Adaptability" },
  { src: k13.url, word: "Trust" },
  { src: k14.url, word: "Leadership" },
];

const rowOne = items.slice(0, 6);
const rowTwo = items.slice(6);

function Item({ src, word }: { src: string; word: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <img
        src={src}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="h-12 w-12 shrink-0 rounded-full object-cover lg:h-14 lg:w-14"
      />
      <span
        className="whitespace-nowrap text-sm font-medium text-muted-foreground/50 lg:text-base"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        {word}
      </span>
    </div>
  );
}

function MarqueeRow({
  row,
  direction,
  reduceMotion,
  variants,
}: {
  row: typeof items;
  direction: "left" | "right";
  reduceMotion: boolean;
  variants: Variants;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [travel, setTravel] = useState(0);
  const [repeats, setRepeats] = useState(1);

  useLayoutEffect(() => {
    const measure = () => {
      const viewport = viewportRef.current;
      const track = trackRef.current;
      if (!viewport || !track) return;
      const last = track.lastElementChild as HTMLElement | null;
      const trailingPad = last ? parseFloat(getComputedStyle(last).paddingRight) || 0 : 0;
      const overflow = track.scrollWidth - viewport.clientWidth;
      if (overflow < 80 && repeats < 4) {
        // Row is narrower than the container: add another pass of items so the
        // track always spans the full width, never exposing empty space.
        setRepeats((r) => r + 1);
        return;
      }
      // Stop short by the trailing padding so the last item ends flush with the edge.
      setTravel(Math.max(0, overflow - trailingPad));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [repeats]);

  const animating = !reduceMotion && travel > 1;
  // ~9px per second keeps the ambient pace regardless of row width.
  const duration = Math.max(30, travel / 9);
  const keyframes = direction === "left" ? [0, -travel] : [-travel, 0];
  const rendered = Array.from({ length: repeats }, () => row).flat();

  return (
    <motion.div variants={variants} className="overflow-hidden" ref={viewportRef}>
      <motion.div
        ref={trackRef}
        className="flex w-max items-center"
        data-marquee={direction}
        style={animating ? undefined : { x: 0 }}
        animate={animating ? { x: keyframes } : undefined}
        transition={
          animating
            ? { duration, ease: "easeInOut", repeat: Infinity, repeatType: "reverse" }
            : undefined
        }
      >
        {rendered.map((item, i) => (
          <div key={`${item.word}-${i}`} className="pr-12 lg:pr-16">
            <Item {...item} />
          </div>
        ))}
      </motion.div>
    </motion.div>
  );
}

export function KeywordAvatars() {
  const reduceMotion = useReducedMotion() ?? false;
  const itemVariants = reduceMotion ? gapItemStatic : gapItem;

  return (
    <section className="hidden bg-background pt-16 pb-0 md:block md:pt-20 md:pb-0">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={gapViewport}
        variants={gapContainer}
        className="w-full"
      >
        <div className="flex flex-col gap-10">
          <MarqueeRow
            row={rowOne}
            direction="right"
            reduceMotion={reduceMotion}
            variants={itemVariants}
          />
          <MarqueeRow
            row={rowTwo}
            direction="left"
            reduceMotion={reduceMotion}
            variants={itemVariants}
          />
        </div>
      </motion.div>
    </section>
  );
}
